import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { BarChart3, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AnalyticsPage = () => {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overview, conversion, reasons] = await Promise.all([
        analyticsAPI.overview({ period }),
        analyticsAPI.conversion({ period }),
        analyticsAPI.declineReasons({ period })
      ]);
      setData({ overview: overview.data, conversion: conversion.data, reasons: reasons.data });
    } catch (err) {
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => `${(amount || 0).toLocaleString('ru-RU')} ₸`;
  const COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-white">Аналитика</h1>
        <div className="flex gap-2">
          {['week', 'month', 'quarter', 'year'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1 rounded-lg text-sm ${period === p ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}>
              {p === 'week' ? 'Неделя' : p === 'month' ? 'Месяц' : p === 'quarter' ? 'Квартал' : 'Год'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="card p-6">
          <p className="text-slate-400 text-sm">Оборот</p>
          <p className="text-2xl font-bold text-white mt-1">{formatAmount(data?.overview?.turnover)}</p>
          <div className={`flex items-center gap-1 mt-2 text-sm ${(data?.overview?.turnover_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {(data?.overview?.turnover_change || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(data?.overview?.turnover_change || 0)}%
          </div>
        </div>
        <div className="card p-6">
          <p className="text-slate-400 text-sm">Платежей</p>
          <p className="text-2xl font-bold text-white mt-1">{data?.overview?.total_payments || 0}</p>
        </div>
        <div className="card p-6">
          <p className="text-slate-400 text-sm">Конверсия</p>
          <p className="text-2xl font-bold text-white mt-1">{(data?.overview?.conversion || 0).toFixed(1)}%</p>
        </div>
        <div className="card p-6">
          <p className="text-slate-400 text-sm">Средний чек</p>
          <p className="text-2xl font-bold text-white mt-1">{formatAmount(data?.overview?.avg_check)}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Динамика оборота</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.conversion?.chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                <Line type="monotone" dataKey="amount" stroke="#22c55e" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Конверсия по дням</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.conversion?.chart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                <Bar dataKey="conversion" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Причины отклонений</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data?.reasons?.data || []} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={80} label>
                    {(data?.reasons?.data || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2">
              {(data?.reasons?.data || []).map((item, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-300 text-sm">{item.reason}</span>
                  </div>
                  <span className="text-white font-medium">{item.count} ({item.percent}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
